import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    TextField,
    MenuItem,
    Button,
    IconButton,
    Alert,
    Divider,
    InputAdornment,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { API_URL } from '../../api';
import YearGroupSelect from '../../components/YearGroupSelect';
import TermSelect from '../../components/TermSelect';
import { shortYear } from '../../constants/yearGroups';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const fmt = (amount, currency = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount) || 0);

const PAYMENT_METHODS = [
    { value: 'both', label: 'Stripe link + bank details' },
    { value: 'stripe', label: 'Stripe link only' },
    { value: 'bank', label: 'Bank details only' },
];

// Build a recipient menu label like "Mark Johnson (Tammy Johnson Yr 9)" for
// parents whose childInfo is filled in, or "Tammy Johnson (Yr 9)" for student
// accounts. Falls back to plain name + email when we have no student data.
const recipientMenuLabel = (r) => {
    const fullName = `${r.firstName || ''} ${r.surname || ''}`.trim();
    const role = r._role || (r.role === 'student' ? 'Student' : 'Parent');
    if (role === 'Student') {
        const yr = shortYear(r.yearGroup);
        const tail = yr ? ` (${yr})` : '';
        return `Student: ${fullName}${tail} — ${r.email}`;
    }
    const childName = r.childInfo?.studentName?.trim() || '';
    const childYr = shortYear(r.childInfo?.yearGroup);
    const childParts = [childName, childYr].filter(Boolean).join(' ');
    const tail = childParts ? ` (${childParts})` : '';
    return `Parent: ${fullName}${tail} — ${r.email}`;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InvoiceCreatePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Default source = enquiry when the page is opened with ?enquiryId=…
    const initialEnquiryId = searchParams.get('enquiryId') || '';
    const initialSource = initialEnquiryId ? 'enquiry' : 'account';
    // When ?duplicateOf=<invoiceId> is present, this page loads that invoice
    // and pre-fills every field so the admin can tweak and save as a new one.
    const duplicateOfId = searchParams.get('duplicateOf') || '';
    // When ?editId=<invoiceId> is present, the page is in EDIT MODE — it
    // pre-fills from that invoice and saves via PATCH back to the same row
    // (rather than POSTing a new draft). Only draft invoices are editable;
    // the backend enforces that, but we also reflect it in the UI title.
    const editingId = searchParams.get('editId') || '';
    // Re-use the duplicate prefill code path — it loads exactly the same
    // fields. The only difference is what we do on Save.
    const prefillSourceId = editingId || duplicateOfId;

    const [recipientSource, setRecipientSource] = useState(initialSource); // 'account' | 'enquiry' | 'inline'
    const [recipients, setRecipients] = useState([]);
    const [parentId, setParentId] = useState('');
    const [enquiries, setEnquiries] = useState([]);
    const [contactEnquiryId, setContactEnquiryId] = useState(initialEnquiryId);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [recipientName, setRecipientName] = useState('');

    const [ccEmails, setCcEmails] = useState([]);
    const [ccDraft, setCcDraft] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [term, setTerm] = useState('');
    const [yearGroup, setYearGroup] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('both');
    const [bankDetails, setBankDetails] = useState('');
    const [lineItems, setLineItems] = useState([{ description: '', qty: 1, unitAmount: 0 }]);
    const [descriptionPresets, setDescriptionPresets] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([
            axios.get(`${API_URL}/api/admin/accounts?role=parent&limit=200`, authHeaders()),
            axios.get(`${API_URL}/api/admin/accounts?role=student&limit=200`, authHeaders()),
        ])
            .then(([parentsRes, studentsRes]) => {
                const parents = (parentsRes.data?.items || []).map((p) => ({ ...p, _role: 'Parent' }));
                const students = (studentsRes.data?.items || []).map((s) => ({ ...s, _role: 'Student' }));
                setRecipients([...parents, ...students]);
            })
            .catch(() => setRecipients([]));

        axios
            .get(`${API_URL}/api/admin/contact-enquiries?limit=200`, authHeaders())
            .then((res) => setEnquiries(res.data?.items || []))
            .catch(() => setEnquiries([]));

        axios
            .get(`${API_URL}/api/admin/invoice-line-templates`, authHeaders())
            .then((res) => setDescriptionPresets(Array.isArray(res.data) ? res.data : []))
            .catch(() => setDescriptionPresets([]));
    }, []);

    // Pre-fill from a source invoice when ?duplicateOf=<id> is present.
    // Carries recipient + line items + term/year + payment method, and clears
    // all "issued" fields (invoice #, ref, status, Stripe IDs, reminder log)
    // by virtue of starting from a fresh draft on submit.
    useEffect(() => {
        if (!prefillSourceId) return;
        let cancelled = false;
        axios
            .get(`${API_URL}/api/admin/invoices/${prefillSourceId}`, authHeaders())
            .then((res) => {
                if (cancelled) return;
                const src = res.data || {};
                // Recipient source detection.
                if (src.parentId?._id || src.parentId) {
                    setRecipientSource('account');
                    setParentId(src.parentId?._id || src.parentId);
                } else if (src.contactEnquiryId?._id || src.contactEnquiryId) {
                    setRecipientSource('enquiry');
                    setContactEnquiryId(src.contactEnquiryId?._id || src.contactEnquiryId);
                } else if (src.recipientEmail) {
                    setRecipientSource('inline');
                    setRecipientEmail(src.recipientEmail || '');
                    setRecipientName(src.recipientName || '');
                }
                setCcEmails(Array.isArray(src.ccEmails) ? src.ccEmails : []);
                setDueDate(src.dueDate ? String(src.dueDate).slice(0, 10) : '');
                setTerm(src.term || '');
                setYearGroup(src.yearGroup || '');
                setPaymentMethod(src.paymentMethod || 'both');
                setBankDetails(src.bankDetails || '');
                if (Array.isArray(src.lineItems) && src.lineItems.length) {
                    setLineItems(
                        src.lineItems.map((i) => ({
                            description: i.description || '',
                            qty: Number(i.qty) || 1,
                            unitAmount: Number(i.unitAmount) || 0,
                        }))
                    );
                }
            })
            .catch(() => setError(editingId
                ? 'Failed to load draft invoice for editing.'
                : 'Failed to load source invoice for duplication.'));
        return () => {
            cancelled = true;
        };
    }, [prefillSourceId, editingId]);

    const addCcEmail = () => {
        const v = String(ccDraft || '').trim().toLowerCase();
        if (!v) return;
        if (!EMAIL_RE.test(v)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (ccEmails.includes(v)) {
            setCcDraft('');
            return;
        }
        setCcEmails((arr) => [...arr, v]);
        setCcDraft('');
        setError('');
    };

    const removeCcEmail = (email) => setCcEmails((arr) => arr.filter((e) => e !== email));

    const updateItem = (idx, key, value) => {
        setLineItems((items) =>
            items.map((it, i) => (i === idx ? { ...it, [key]: value } : it))
        );
    };

    const addItem = () =>
        setLineItems((items) => [...items, { description: '', qty: 1, unitAmount: 0 }]);

    const removeItem = (idx) => setLineItems((items) => items.filter((_, i) => i !== idx));

    const subtotal = lineItems.reduce(
        (acc, i) => acc + (Number(i.qty) || 1) * (Number(i.unitAmount) || 0),
        0
    );

    const submit = async (alsoSend) => {
        setBusy(true);
        setError('');
        try {
            const payload = {
                ccEmails,
                lineItems: lineItems
                    .filter((i) => String(i.description || '').trim())
                    .map((i) => ({
                        description: String(i.description).trim(),
                        qty: Number(i.qty) || 1,
                        unitAmount: Number(i.unitAmount) || 0,
                    })),
                dueDate: dueDate || undefined,
                term: term || undefined,
                yearGroup: yearGroup || undefined,
                paymentMethod,
                bankDetails,
            };
            if (recipientSource === 'account') {
                if (!parentId) { setError('Please pick a registered parent or student.'); setBusy(false); return; }
                payload.parentId = parentId;
            } else if (recipientSource === 'enquiry') {
                if (!contactEnquiryId) { setError('Please pick a contact enquiry.'); setBusy(false); return; }
                payload.contactEnquiryId = contactEnquiryId;
            } else {
                if (!recipientEmail.trim()) { setError('Please enter a recipient email.'); setBusy(false); return; }
                payload.recipientEmail = recipientEmail.trim();
                payload.recipientName = recipientName.trim();
            }
            let invoiceId;
            if (editingId) {
                const { data: updated } = await axios.patch(
                    `${API_URL}/api/admin/invoices/${editingId}`,
                    payload,
                    authHeaders()
                );
                invoiceId = updated._id;
            } else {
                const { data: created } = await axios.post(
                    `${API_URL}/api/admin/invoices`,
                    payload,
                    authHeaders()
                );
                invoiceId = created._id;
            }
            if (alsoSend) {
                await axios.post(
                    `${API_URL}/api/admin/invoices/${invoiceId}/send`,
                    {},
                    authHeaders()
                );
            }
            navigate(editingId ? `/invoices/${invoiceId}` : '/invoices');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Container>
            <Typography variant="h4" sx={{ mb: 3 }}>
                {editingId ? 'Edit draft invoice' : duplicateOfId ? 'Duplicate invoice' : 'New invoice'}
            </Typography>
            {editingId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Editing a <strong>draft</strong>. The invoice number and payment reference are
                    kept — change line items, recipient, dates, etc. and save.
                </Alert>
            )}
            {!editingId && duplicateOfId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Pre-filled from an existing invoice. Adjust anything you need (term, dates,
                    line items…) — a fresh invoice number and payment reference will be assigned
                    on save.
                </Alert>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ p: 3, mb: 3 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Recipient source
                        </Typography>
                        <ToggleButtonGroup
                            value={recipientSource}
                            exclusive
                            onChange={(_, v) => v && setRecipientSource(v)}
                            size="small"
                        >
                            <ToggleButton value="account">Registered account</ToggleButton>
                            <ToggleButton value="enquiry">Contact enquiry</ToggleButton>
                            <ToggleButton value="inline">Enter manually</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {recipientSource === 'account' && (
                        <TextField
                            select
                            label="Parent / Student"
                            value={parentId}
                            onChange={(e) => setParentId(e.target.value)}
                            size="small"
                            required
                        >
                            <MenuItem value="">— Select recipient —</MenuItem>
                            {recipients.map((r) => (
                                <MenuItem key={r._id} value={r._id}>
                                    {recipientMenuLabel(r)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {recipientSource === 'enquiry' && (
                        <TextField
                            select
                            label="Contact enquiry"
                            value={contactEnquiryId}
                            onChange={(e) => setContactEnquiryId(e.target.value)}
                            size="small"
                            required
                            helperText="Picks the parent's name + email from the enquiry. The new invoice links back to it."
                        >
                            <MenuItem value="">— Select enquiry —</MenuItem>
                            {enquiries.map((q) => (
                                <MenuItem key={q._id} value={q._id}>
                                    {new Date(q.createdAt).toLocaleDateString('en-GB')} · {q.firstName} {q.surname} ({q.email}) · {q.enquiryType}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {recipientSource === 'inline' && (
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Recipient name"
                                value={recipientName}
                                onChange={(e) => setRecipientName(e.target.value)}
                                size="small"
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="Recipient email"
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                size="small"
                                required
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                    )}

                    <Box>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                                label="Also send to (email)"
                                value={ccDraft}
                                onChange={(e) => setCcDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        addCcEmail();
                                    }
                                }}
                                size="small"
                                sx={{ flex: 1 }}
                                helperText="Press Enter to add. Each email gets a copy of the invoice."
                            />
                            <Button onClick={addCcEmail} sx={{ mt: '4px' }}>
                                Add
                            </Button>
                        </Stack>
                        {ccEmails.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {ccEmails.map((e) => (
                                    <Chip key={e} label={e} size="small" onDelete={() => removeCcEmail(e)} />
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            type="date"
                            label="Due date"
                            InputLabelProps={{ shrink: true }}
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                        <TermSelect
                            value={term}
                            onChange={setTerm}
                            anyLabel="— None —"
                            helperText="Which academic term this invoice covers (optional)."
                            sx={{ flex: 1 }}
                        />
                        <YearGroupSelect
                            value={yearGroup}
                            onChange={setYearGroup}
                            anyLabel="— None —"
                            helperText="Used for filtering. Auto-pulled from registered accounts if left blank."
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                    <TextField
                        select
                        label="Payment method"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        size="small"
                    >
                        {PAYMENT_METHODS.map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                                {p.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {(paymentMethod === 'bank' || paymentMethod === 'both') && (
                        <TextField
                            multiline
                            minRows={3}
                            label="Bank details (printed on invoice)"
                            value={bankDetails}
                            onChange={(e) => setBankDetails(e.target.value)}
                            helperText="Leave blank to use the defaults from Settings → Bank Transfer Details. Anything you type here overrides them for this invoice only."
                        />
                    )}
                </Stack>
            </Card>

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Line items
                </Typography>
                <Stack spacing={2}>
                    {lineItems.map((item, idx) => (
                        <Stack direction="row" spacing={1} key={idx} alignItems="center">
                            <Autocomplete
                                freeSolo
                                options={descriptionPresets.map((p) => p.description)}
                                value={item.description}
                                onChange={(_, v) => updateItem(idx, 'description', v || '')}
                                onInputChange={(_, v) => updateItem(idx, 'description', v || '')}
                                size="small"
                                sx={{ flex: 1 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Description"
                                        placeholder="Type or pick a preset"
                                    />
                                )}
                            />
                            <TextField
                                label="Qty"
                                type="number"
                                value={item.qty}
                                onChange={(e) => updateItem(idx, 'qty', e.target.value)}
                                size="small"
                                sx={{ width: 80 }}
                                inputProps={{ min: 1, step: 1 }}
                            />
                            <TextField
                                label="Unit price"
                                type="number"
                                value={item.unitAmount}
                                onChange={(e) => updateItem(idx, 'unitAmount', e.target.value)}
                                size="small"
                                sx={{ width: 160 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <IconButton onClick={() => removeItem(idx)} disabled={lineItems.length === 1}>
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button onClick={addItem} startIcon={<AddIcon />}>
                        Add line item
                    </Button>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6">Total: {fmt(subtotal)}</Typography>
                </Box>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => submit(false)} disabled={busy} variant="outlined">
                    {editingId ? 'Save changes' : 'Save as draft'}
                </Button>
                <Button onClick={() => submit(true)} disabled={busy} variant="contained">
                    {busy ? 'Working…' : (editingId ? 'Save and send' : 'Save and send')}
                </Button>
            </Box>
        </Container>
    );
}
